import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular-boost';
import { Observable } from 'rxjs';
import { catchError, filter, map, startWith } from 'rxjs/operators';
import { IFunnel, IFunnelStep, IUtility } from '../interfaces/graph-types';

type ErrorCallback = (err: string) => any;
type SuccessCallback = (data: any) => any;

@Injectable()
export class UtilityService {
  // ------------------------------------------------------
  constructor(private apollo: Apollo) {}

  // ------------------------------------------------------
  public queryGraphQLCacheFirst(query, variables): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: false,
    }).valueChanges;
  }

  // ------------------------------------------------------
  public queryGraphQL(query, variables): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      // About fetch policies: https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-config-options-fetchPolicy
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false,
    }).valueChanges;
  }

  // ------------------------------------------------------
  public queryGraphQLNoCache(query, variables): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    }).valueChanges;
  }
  
  // ------------------------------------------------------
  public getJobSpec(funnelId: string): Observable<any> {
    const variables = {
      funnelId,
    };
    let query = gql`
      query getJobSpec($funnelId: ID!) {
        funnelGuest(funnelId: $funnelId) {
          id
          name
          jobSpec
          jobSpecTitle
        }
      }
    `;
    return this.queryGraphQLNoCache(query, variables)
    .pipe(
      filter(response => response.data),
      map(({ data }) => data.funnelGuest)
      // startWith(null),
    );
  }

  // ------------------------------------------------------
  public getApplicationStep(applicationId: string): Observable<any> {
    const variables = {
      applicationId,
    };
    let query = gql`
      query applicationStep($applicationId: ID!) {
        application(applicationId: $applicationId) {
          id
          funnel {
            id
            jobSpecTitle
          }
          stage
          currentStepVariation {
            type
            required
            statement
            descriptionHtml
            minSelection
            maxSelection
            choices
            minScore
            manualReview
            explanation
          }
        }
      }
    `;
    return this.queryGraphQLNoCache(query, variables)
    .pipe(
      filter(response => response.data),
      map(({ data }) => data.application)
      // startWith(null),
    );
  }

  // ------------------------------------------------------
  public startFunnel(
    funnelId: string,
    internetSpeed: number,
    source: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    this.apollo
      .mutate({
        mutation: gql`
          mutation startFunnel($input: StartFunnelInput!) {
            startFunnel(input: $input) {
              error
              applicationId
            }
          }
        `,
        variables: {
          input: {
            funnelId,
            internetSpeed,
            source
          },
        },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        console.log(result);
        let data = result['data'].startFunnel;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public continueApplication(
    applicationId: string,
    response: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    this.apollo
      .mutate({
        mutation: gql`
          mutation continueApplication($input: ContinueApplicationInput!) {
            continueApplication(input: $input) {
              error
            }
          }
        `,
        variables: {
          input: {
            applicationId,
            response,
          },
        },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        console.log(result);
        let data = result['data'].continueApplication;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public continueApplicationMulti(
    applicationId: string,
    response: string[],
    explanation: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    this.apollo
      .mutate({
        mutation: gql`
          mutation continueApplicationMulti($input: ContinueApplicationMultiInput!) {
            continueApplicationMulti(input: $input) {
              error
            }
          }
        `,
        variables: {
          input: {
            applicationId,
            response,
            explanation
          },
        },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        console.log(result);
        let data = result['data'].continueApplicationMulti;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }


  // ------------------------------------------------------
  getInboxPricing(plan: string): number {
    let priceInbox = 29;

    if (plan == 'pro') {
      priceInbox = 39;
    }
    if (plan == 'expert') {
      priceInbox = 59;
    }

    return priceInbox;
  }

  // ------------------------------------------------------
  getPricing(plan: string, inboxes: number, billing: string): number {
    if (plan == 'sleep') {
      if (billing === 'yearly') {
        return 60;
      } else {
        return 5;
      }
    }

    let price = 49;
    let priceInbox = this.getInboxPricing(plan);

    if (plan == 'pro') {
      price = 69;
    }
    if (plan == 'expert') {
      price = 99;
    }

    price = price + (inboxes - 1) * priceInbox;

    if (billing === 'yearly') {
      price = price * 10;
    }

    return price;
  }

  // ------------------------------------------------------
  getPricingWithDiscount(price: number, discount: number = 0): number {
    const factor = 1 - discount / 10000;

    return price * factor;
  }

  // -------------------------------------------------------------------
  formatPrice(price: number, billing: string): string {
    const formatted = this.toLocaleWithMax2Decimals(price);

    if (billing === 'yearly') {
      return '$' + formatted + ' / year';
    }

    return '$' + formatted + ' / month';
  }

  // ------------------------------------------------------
  toLocaleWithMax2Decimals(n: number): string {
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  // ------------------------------------------------------
  orgaInboxInvite(orgId: string, secret: string): Observable<IUtility> {
    return this.apollo
      .watchQuery<{ utility: IUtility }>({
        variables: { orgId: orgId, secret: secret },
        query: gql`
          query organizationInvite($orgId: ID!, $secret: String!) {
            utility {
              organizationInvite(orgId: $orgId, secret: $secret) {
                id
                secret
                organizationName
                fromName
                fromEmail
                fromAvatarUrl
              }
            }
          }
        `,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(map(res => res.data.utility));
  }

  // ------------------------------------------------------
  auditScopeIcons(): any {
    return {
      reply: 'fad fa-reply',
      account: 'fad fa-home',
      dnc: 'fad fa-times-octagon',
      schedule: 'fad fa-clock',
      inbox: 'fad fa-mailbox',
      application: 'fad fa-hiking',
      group: 'fad fa-fill-drip',
      timetable: 'fad fa-business-time',
      import: 'fad fa-file-import',
      export: 'fad fa-file-export',
      label: 'fad fa-flag',
      funnel: 'fad fa-book-spells',
      company: 'fad fa-building',
      candidate: 'fad fa-user',
      tag: 'fad fa-tag',
      task: 'fad fa-clipboard-check',
      draft: 'fad fa-scroll',
      subscription: 'fad fa-shopping-cart',
      billing: 'fad fa-money-bill-wave',
      todo: 'fad fa-check-square',
      autoimport: 'fad fa-cloud-download',
      default: 'fad fa-bullseye-pointer',
    };
  }

  // ------------------------------------------------------
  public getTimeZones(): Observable<IUtility> {
    let variables = {};
    let query = gql`
      query getTimeZones {
        utility {
          timeZones {
            id
            name
            offset
            offsetNow
            mappingValue
          }
        }
      }
    `;
    return this.queryGraphQLCacheFirst(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.utility),
    );
  }

  // ------------------------------------------------------
  public completeSetup(
    orgId: string,
    name: string,
    url: string,
    clients: string,
    customers: string,
    employees: string,
    referral: string,
    extra: string,
    affiliate: string,
    role: string,
    roleOther: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ): null {
    const input = {
      orgId,
      name,
      url,
      clients,
      customers,
      employees,
      referral,
      extra,
      affiliate,
      role,
      roleOther,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation completeSetup($input: CompleteSetupInput!) {
            completeSetup(input: $input) {
              error
              skipBilling
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].completeSetup;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }
}
